import React, { useMemo, useState } from 'react';
import { Calendar, Message, Panel, Placeholder, Tag } from 'rsuite';
import enUS from 'rsuite/locales/en_US';
import { initializeIcons } from '@fluentui/font-icons-mdl2';
import { getIconClassName } from '@fluentui/style-utilities';
import TimeOffStatusBadge from '../components/timeoff/TimeOffStatusBadge';
import PageHeader from '../components/shared/PageHeader';
import { useCloseDates, useTimeOffRequests } from '../hooks/useQueries';
import './TimeOffPage.css';

initializeIcons();
const sharePointLogoIconClass = getIconClassName('SharepointLogo');
const SHAREPOINT_TIME_OFF_CALENDAR_URL =
  'https://nsieng.sharepoint.com/sites/SpacePort/Lists/NSI%20Time%20Off%20Calendar/calendar.aspx';

const normalizeDateOnly = (value) => {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

const parseLocalDateOnly = (value) => {
  if (!value) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }
  const s = String(value).trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

const formatDateOnly = (d) => {
  const date = normalizeDateOnly(d);
  if (!date) return '';
  const pad2 = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
};

const formatLongDate = (value) => {
  const date = normalizeDateOnly(value);
  if (!date) return '-';
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

const getEmployeeLabel = (request) => {
  const firstName = request.first_name || request.employee_first_name || '';
  const lastName = request.last_name || request.employee_last_name || '';
  const fullName = `${String(firstName).trim()} ${String(lastName).trim()}`.trim();
  return fullName || request.email || request.employee_email || 'Unknown User';
};

const getRequestEntryType = (status) => {
  const normalizedStatus = String(status || '').toLowerCase();
  if (normalizedStatus === 'approved') return 'approved';
  if (normalizedStatus === 'pending') return 'pending';
  return 'other';
};

const getRequestTypeIconClass = (requestType) => {
  const normalizedType = String(requestType || '').toLowerCase();
  if (normalizedType === 'vacation') return 'bi bi-airplane-fill';
  if (normalizedType === 'sick') return 'bi bi-heart-pulse-fill';
  if (normalizedType === 'reduction') return 'bi bi-clock-fill';
  return 'bi bi-calendar-event';
};

const isSameMonth = (a, b) => {
  const left = normalizeDateOnly(a);
  const right = normalizeDateOnly(b);
  if (!left || !right) return false;
  return left.getFullYear() === right.getFullYear() && left.getMonth() === right.getMonth();
};

export default function TimeOffCalendarViewPage() {
  const requestsQuery = useTimeOffRequests({ enabled: true });
  const closeDatesQuery = useCloseDates({ enabled: true });
  const [selectedDate, setSelectedDate] = useState(normalizeDateOnly(new Date()));
  const [calendarPageDate, setCalendarPageDate] = useState(normalizeDateOnly(new Date()));

  const today = normalizeDateOnly(new Date());

  const dayDetailsMap = useMemo(() => {
    const requests = requestsQuery.data || [];
    const closeDates = closeDatesQuery.data || [];
    const map = new Map();

    const ensureRow = (key) => {
      if (!map.has(key)) {
        map.set(key, { requestEntries: [], closeDateNames: [] });
      }
      return map.get(key);
    };

    for (const request of requests) {
      const start = parseLocalDateOnly(request.start_date);
      const end = parseLocalDateOnly(request.end_date);
      if (!start || !end || end < start) continue;

      const status = String(request.status || '').toLowerCase();
      if (status === 'cancelled' || status === 'rejected') continue;

      const employeeLabel = getEmployeeLabel(request);

      for (let cursor = new Date(start); cursor <= end; cursor.setDate(cursor.getDate() + 1)) {
        const key = formatDateOnly(cursor);
        if (!key) continue;
        const row = ensureRow(key);
        row.requestEntries.push({
          id: request.id,
          employeeLabel,
          requestType: request.request_type,
          status: request.status,
        });
      }
    }

    for (const closeDate of closeDates) {
      const ranges = Array.isArray(closeDate?.resolved_dates) ? closeDate.resolved_dates : [];
      for (const range of ranges) {
        const start = parseLocalDateOnly(range?.startDate);
        const end = parseLocalDateOnly(range?.endDate || range?.startDate);
        if (!start || !end || end < start) continue;

        for (let cursor = new Date(start); cursor <= end; cursor.setDate(cursor.getDate() + 1)) {
          const key = formatDateOnly(cursor);
          if (!key) continue;
          const row = ensureRow(key);
          const closeDateName = String(closeDate?.name || '').trim();
          if (closeDateName && !row.closeDateNames.includes(closeDateName)) {
            row.closeDateNames.push(closeDateName);
          }
        }
      }
    }

    return map;
  }, [closeDatesQuery.data, requestsQuery.data]);

  const selectedKey = formatDateOnly(selectedDate);
  const selectedDayDetails = selectedKey ? dayDetailsMap.get(selectedKey) : null;
  const dayDetailItems = useMemo(() => {
    const selectedRequests = selectedDayDetails?.requestEntries || [];
    const selectedCloseDates = selectedDayDetails?.closeDateNames || [];

    return [
      ...selectedRequests.map((entry, index) => ({
        kind: 'request',
        key: `request-${entry.id}-${index}`,
        title: entry.requestType,
        subtitle: entry.employeeLabel,
        status: entry.status,
        iconClass: getRequestTypeIconClass(entry.requestType),
      })),
      ...selectedCloseDates.map((name, index) => ({
        kind: 'closeDate',
        key: `close-date-${name}-${index}`,
        title: 'Close Date',
        subtitle: name,
      })),
    ];
  }, [selectedDayDetails]);

  if (requestsQuery.isLoading || closeDatesQuery.isLoading) return <Placeholder.Paragraph rows={6} active />;

  if (requestsQuery.error || closeDatesQuery.error) {
    return (
      <Message type="error" header="Error" showIcon>
        {String(requestsQuery.error?.message || closeDatesQuery.error?.message || requestsQuery.error || closeDatesQuery.error)}
      </Message>
    );
  }

  return (
    <div className="bdc-page">
      <PageHeader
        crumbs={[
          { label: 'Home', to: '/' },
          { label: 'Time Off', to: '/time-off' },
          { label: 'Calendar' },
        ]}
        icon="bi-calendar-week"
        title="Time Off Calendar"
        actions={(
          <>
            <button
              type="button"
              className="bdc-btn bdc-btn--primary"
              onClick={() => window.open(SHAREPOINT_TIME_OFF_CALENDAR_URL, '_blank', 'noopener,noreferrer')}
            >
              View in SharePoint
              <i className={sharePointLogoIconClass} aria-hidden="true" />
            </button>
          </>
        )}
      />

      <Panel bordered className="timeoff-create-calendar-panel">
        <div className="timeoff-range-label">Select a day to view details.</div>

        <div className="timeoff-large-calendar-wrap">
          <div className="timeoff-calendar-legend">
            <span className="timeoff-calendar-legend-item"><span className="timeoff-calendar-dot timeoff-calendar-dot-close" />Close Date</span>
            <span className="timeoff-calendar-legend-item"><span className="timeoff-calendar-dot timeoff-calendar-dot-approved" />Approved</span>
            <span className="timeoff-calendar-legend-item"><span className="timeoff-calendar-dot timeoff-calendar-dot-pending" />Pending</span>
          </div>

          <Calendar
            locale={enUS}
            isoWeek={false}
            weekStart={0}
            bordered
            value={calendarPageDate || new Date()}
            onMonthChange={(date) => setCalendarPageDate(normalizeDateOnly(date))}
            onSelect={(date) => {
              const next = normalizeDateOnly(date);
              setSelectedDate(next);

              // Keep selection in sync with the Calendar's controlled value, but
              // do not auto-jump months when selecting overflow days.
              if (isSameMonth(next, calendarPageDate)) {
                setCalendarPageDate(next);
              }
            }}
            className="timeoff-large-calendar"
            renderCell={(date) => {
              const key = formatDateOnly(date);
              const details = key ? dayDetailsMap.get(key) : null;
              const closeDateNames = details?.closeDateNames || [];
              const requestEntries = details?.requestEntries || [];
              const isTodayCell = formatDateOnly(date) === formatDateOnly(today);
              const isSelectedCell = selectedKey && selectedKey === key;

              const signalEntries = [
                ...closeDateNames.map((label) => ({ type: 'close', label })),
                ...requestEntries.map((entry) => ({
                  type: getRequestEntryType(entry.status),
                  label: `Time off - ${entry.employeeLabel}`,
                })),
              ];

              const visibleSignalEntries = signalEntries.slice(0, 2);
              const remainingSignalCount = signalEntries.length - visibleSignalEntries.length;

              if (!isTodayCell && !isSelectedCell && signalEntries.length === 0) {
                return null;
              }

              return (
                <div className="timeoff-range-cell-markers">
                  {isSelectedCell ? <span className="timeoff-day-selected-outline" /> : null}
                  {isTodayCell ? <span className="timeoff-marker timeoff-marker-today timeoff-marker-today-corner">Today</span> : null}

                  {visibleSignalEntries.length > 0 ? (
                    <div className="timeoff-calendar-info-lines">
                      {visibleSignalEntries.map((entry, index) => (
                        <div key={`${entry.type}-${entry.label}-${index}`} className="timeoff-calendar-info-line" title={entry.label}>
                          <span
                            className={`timeoff-calendar-dot ${
                              entry.type === 'close'
                                ? 'timeoff-calendar-dot-close'
                                : entry.type === 'approved'
                                  ? 'timeoff-calendar-dot-approved'
                                  : 'timeoff-calendar-dot-pending'
                            }`}
                          />
                          <span className="timeoff-calendar-info-line-text">{entry.label}</span>
                        </div>
                      ))}
                      {remainingSignalCount > 0 ? (
                        <div className="timeoff-calendar-info-line text-muted">+{remainingSignalCount} more</div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              );
            }}
          />
        </div>
      </Panel>

      <Panel bordered>
        <div className="d-flex align-items-center justify-content-between gap-2 flex-wrap mb-2">
          <div className="fw-semibold">Day Details</div>
          <Tag size="md" appearance="ghost">{formatLongDate(selectedDate)}</Tag>
        </div>

        {dayDetailItems.length === 0 ? (
          <div className="text-muted">No time off or close dates for this day.</div>
        ) : null}

        {dayDetailItems.length > 0 ? (
          <div className="row g-2">
            {dayDetailItems.map((item) => (
              <div key={item.key} className="col-12 col-sm-6 col-xl-3">
                <div className="p-2 h-100 d-flex align-items-start justify-content-between gap-3">
                  <div>
                    <div className="fw-semibold d-flex align-items-center gap-2" style={{ textTransform: item.kind === 'request' ? 'capitalize' : 'none' }}>
                      {item.kind === 'request' ? <i className={item.iconClass} aria-hidden="true" /> : null}
                      {item.kind === 'closeDate' ? <span aria-hidden="true">🌴</span> : null}
                      <span>{item.title}</span>
                    </div>
                    <div className="text-muted" style={{ fontSize: 13 }}>{item.subtitle}</div>
                  </div>
                  {item.kind === 'request' && String(item.status || '').toLowerCase() === 'pending' ? (
                    <TimeOffStatusBadge status={item.status} approvalReason={item.approval_reason} />
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </Panel>
    </div>
  );
}
